import type { DependencyList } from "react";
import { createLocalPersister } from "tinybase/persisters/persister-browser/with-schemas";
import * as UIReact from "tinybase/ui-react/with-schemas";
import {
    type Value,
    type Values,
    type Store
} from "tinybase/with-schemas";

import {
    APP_VALUES_SCHEMA,
    type AppSchema,
} from "@/stores/schemas";

const STORE_NAME = "app";

const {
    useCreatePersister,
    useProvideStore,
    useValue,
    useSetValueCallback,
    useStore,
} = UIReact as UIReact.WithSchemas<AppSchema>;

type ValueIDs = keyof typeof APP_VALUES_SCHEMA;

export const useAppStore = () => {
    return useStore();
}

export const useAppValue = <ValueID extends ValueIDs>(valueID: ValueID) => {
    return useValue(valueID, STORE_NAME);
}

export const useSetAppValueCallback = <Parameter, ValueID extends ValueIDs>
    (
        valueID: ValueID,
        getValue: (parameter: Parameter) => Value<AppSchema[1], ValueID>,
        getValueDeps?: DependencyList
    ) => {
    useSetValueCallback(valueID, getValue, getValueDeps, STORE_NAME);
}

export const AppStore = ({ store }: { store: Store<AppSchema> }) => {
    useCreatePersister(
        store,
        (store) => createLocalPersister(store, STORE_NAME),
        [],
        async (persister) => {
            await persister.startAutoLoad();
            await persister.startAutoSave();
        }
    );

    useProvideStore(STORE_NAME, store);
    return null;
}
